import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Loader2, FolderOpen, Save, Trash2, Sparkles, ShieldCheck, User as UserIcon, FileCheck2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PlannerTemplateDialog
 * - mode="browse": shows list of templates with Apply + Delete
 * - mode="save": captures current planner state into a new user template
 * onApply(template) — called after user confirms applying a template
 */
export function PlannerTemplateDialog({
  open, onOpenChange, mode, onApply,
  currentSettings, isSuperAdmin,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null); // template pending confirmation
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const [saveAsSystem, setSaveAsSystem] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/planner/templates`, { withCredentials: true });
      setTemplates(res.data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && mode === 'browse') fetchTemplates();
    if (open && mode === 'save') {
      setSaveName(''); setSaveDesc(''); setSaveAsSystem(false);
    }
    if (!open) setConfirming(null);
  }, [open, mode, fetchTemplates]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/planner/templates/${id}`, { withCredentials: true });
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const confirmApply = async () => {
    if (!confirming) return;
    onApply(confirming);
    // Fire-and-forget apply event so super-admin analytics reflect real usage.
    try {
      await axios.post(
        `${API_URL}/api/planner/templates/${confirming.id}/apply`,
        {
          estimate_id: currentSettings?.estimate_id || null,
          estimate_total_effort: currentSettings?.estimate_total_effort || null,
          estimate_total_cost: currentSettings?.estimate_total_cost || null,
        },
        { withCredentials: true },
      );
    } catch { /* analytics is best-effort */ }
    toast.success(`Applied "${confirming.name}"`);
    setConfirming(null);
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!saveName.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: saveName.trim(),
        description: saveDesc.trim(),
        scope: saveAsSystem && isSuperAdmin ? 'system' : 'user',
        ...currentSettings,
      };
      await axios.post(`${API_URL}/api/planner/templates`, payload, { withCredentials: true });
      toast.success('Template saved');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // ── SAVE MODE ──
  if (mode === 'save') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="template-save-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
              <Save className="w-4 h-4" /> Save as Template
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Capture your current Planner settings so you can reuse them on future estimates.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Template Name</Label>
              <Input value={saveName} onChange={e => setSaveName(e.target.value)}
                placeholder="e.g. Healthcare LMS — Quick Start" maxLength={120}
                data-testid="template-save-name-input" />
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Textarea value={saveDesc} onChange={e => setSaveDesc(e.target.value)}
                placeholder="When to use this template; key tuning decisions…"
                rows={3} data-testid="template-save-desc-input" />
            </div>
            {isSuperAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={saveAsSystem} onChange={e => setSaveAsSystem(e.target.checked)}
                  data-testid="template-save-system-checkbox" />
                <ShieldCheck className="w-3.5 h-3.5 text-[#4ABFCF]" />
                Save as curated system template (visible to everyone)
              </label>
            )}
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2.5 text-[11px] text-slate-600 space-y-1">
              <div className="font-semibold text-[#0F2040]">This template will capture:</div>
              <div>• Resource FTE: {Object.keys(currentSettings?.resource_overrides || {}).length} roles</div>
              <div>• Working assumptions: {currentSettings?.hours_per_day}h/day × {currentSettings?.days_per_week}d/week @ {currentSettings?.utilization_pct}% utilization</div>
              <div>• Project duration: {currentSettings?.project_weeks ? `${currentSettings.project_weeks} weeks` : 'Auto'}</div>
              <div>• Complexity week presets</div>
              <div>• Modifier snapshot (informational)</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="template-save-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !saveName.trim()}
              className="bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="template-save-submit-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ── BROWSE MODE ──
  const systemTpls = templates.filter(t => t.scope === 'system');
  const personalTpls = templates.filter(t => t.scope === 'user');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-browse-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
            <FolderOpen className="w-4 h-4" /> Planner Template Library
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Apply a curated or personal template to pre-fill resource defaults, duration, and complexity presets.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#0F2040]" /></div>
        ) : (
          <div className="space-y-4">
            {systemTpls.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#4ABFCF]" />
                  <h3 className="text-xs font-bold text-[#0F2040] uppercase tracking-wide">Seertech Curated</h3>
                </div>
                <div className="space-y-2">
                  {systemTpls.map(t => (
                    <TemplateCard key={t.id} template={t} onApply={() => setConfirming(t)}
                      onDelete={isSuperAdmin && !t.is_seed ? () => handleDelete(t.id) : null} />
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                <h3 className="text-xs font-bold text-[#0F2040] uppercase tracking-wide">My Templates</h3>
              </div>
              {personalTpls.length === 0 ? (
                <div className="text-xs text-slate-400 italic py-3 pl-5 border-l-2 border-slate-100">
                  No personal templates yet. Click "Save as Template" in the Planner to create one.
                </div>
              ) : (
                <div className="space-y-2">
                  {personalTpls.map(t => (
                    <TemplateCard key={t.id} template={t} onApply={() => setConfirming(t)}
                      onDelete={() => handleDelete(t.id)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirmation dialog */}
        <Dialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
          <DialogContent data-testid="template-apply-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
                <FileCheck2 className="w-4 h-4" /> Apply Template?
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Review the change below before confirming.
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm text-slate-600 space-y-2">
              <p>
                Replace your current Planner settings with <strong className="text-[#0F2040]">{confirming?.name}</strong>?
              </p>
              <p className="text-xs text-slate-400">
                Your FTE, working assumptions, duration preset, and complexity weeks will be overwritten.
                Your saved estimate is not modified. You can regenerate the timeline afterwards.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setConfirming(null)} data-testid="template-apply-cancel-btn">Cancel</Button>
              <Button size="sm" onClick={confirmApply}
                className="bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="template-apply-confirm-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Apply Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function TemplateCard({ template, onApply, onDelete }) {
  const fteCount = Object.keys(template.resource_overrides || {}).length;
  return (
    <Card className="hover:border-[#0F2040]/30 transition-colors" data-testid={`template-card-${template.id}`}>
      <CardContent className="py-2.5 px-3 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-sm font-semibold text-[#0F2040] truncate">{template.name}</span>
            {template.is_seed && <Badge variant="secondary" className="text-[9px] h-4 px-1.5 bg-[#4ABFCF]/10 text-[#4ABFCF] border-[#4ABFCF]/20">Built-in</Badge>}
          </div>
          {template.description && (
            <p className="text-[11px] text-slate-500 line-clamp-2">{template.description}</p>
          )}
          <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
            {template.project_weeks && <span>{template.project_weeks}w</span>}
            {fteCount > 0 && <span>· {fteCount} roles</span>}
            {template.utilization_pct && <span>· {template.utilization_pct}% util</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" onClick={onApply}
            className="bg-[#0F2040] hover:bg-[#1A3055] text-white text-[10px] h-7 px-2.5"
            data-testid={`template-apply-btn-${template.id}`}>
            <Sparkles className="w-3 h-3 mr-1" /> Apply
          </Button>
          {onDelete && (
            <Button size="sm" variant="ghost" onClick={onDelete}
              className="text-slate-400 hover:text-red-600 h-7 px-1.5"
              data-testid={`template-delete-btn-${template.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
